import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Grid, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const SupportCenter = () => {
  const [view, setView] = useState("assistant");
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [article, setArticle] = useState(null);
  const [excludedIds, setExcludedIds] = useState([]);
  const [consultedTitles, setConsultedTitles] = useState([]);
  const [round, setRound] = useState(0);
  const [step, setStep] = useState("start");
  const [humanSubject, setHumanSubject] = useState("");
  const [humanMessage, setHumanMessage] = useState("");
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadConversations = useCallback(async (preferredId = null) => {
    try {
      const payload = await saasApi.getSupportConversations({ page: 1, limit: 50 });
      setConversations(payload.data || []);
      setSelected((current) => payload.data?.find((item) => Number(item.id) === Number(preferredId)) || current || payload.data?.[0] || null);
      setError("");
    } catch (err) { setError(err.message); }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const payload = await saasApi.getSupportMessages(selected.id, { page: 1, limit: 100 });
      setMessages(payload.data || []);
    } catch (err) { setError(err.message); }
  }, [selected]);

  useEffect(() => {
    loadConversations();
    const id = setInterval(() => { if (document.visibilityState === "visible") loadConversations(); }, 10000);
    return () => clearInterval(id);
  }, [loadConversations]);
  useEffect(() => {
    if (view !== "conversations") return undefined;
    loadMessages();
    const id = setInterval(() => { if (document.visibilityState === "visible") loadMessages(); }, 4000);
    return () => clearInterval(id);
  }, [loadMessages, view]);

  const findSolutions = async (text, excluded = excludedIds) => {
    const clean = text.trim();
    if (!clean) return;
    setBusy(true);
    setError("");
    setArticle(null);
    try {
      const results = await saasApi.searchSupportKnowledge({ query: clean, exclude_ids: excluded });
      setSuggestions(results);
      setStep("results");
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const startSearch = async () => {
    setExcludedIds([]);
    setConsultedTitles([]);
    setRound(0);
    setQuery(query.trim());
    await findSolutions(query, []);
  };

  const openArticle = async (item) => {
    setArticle(item);
    setConsultedTitles((current) => current.includes(item.title) ? current : [...current, item.title]);
    const nextExcluded = [...new Set([...excludedIds, Number(item.id)])];
    setExcludedIds(nextExcluded);
    setStep("answer");
    const relatedQuery = item.related_terms?.length ? item.related_terms.join(" ") : item.category;
    try {
      const results = await saasApi.searchSupportKnowledge({ query: relatedQuery, exclude_ids: nextExcluded });
      setSuggestions(results);
    } catch (_) { setSuggestions([]); }
  };

  const showOtherOptions = async () => {
    if (round >= 1) {
      setStep("escalation");
      setArticle(null);
      return;
    }
    setRound(1);
    setArticle(null);
    setBusy(true);
    try {
      setCategories(await saasApi.getSupportKnowledgeCategories());
      setStep("categories");
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const startHumanSupport = () => {
    setHumanSubject(article?.title || "Necesito ayuda con el dashboard");
    setHumanMessage("");
    setStep("human");
  };

  const createHumanConversation = async () => {
    if (!humanSubject.trim() || !humanMessage.trim()) return;
    setBusy(true);
    try {
      const explored = `\n\nConsulta inicial: ${query}.${consultedTitles.length ? ` Soluciones consultadas: ${consultedTitles.join("; ")}.` : ""}`;
      const created = await saasApi.createSupportConversation({
        subject: humanSubject.trim(),
        message: `${humanMessage.trim()}${explored}`,
        category: "TECHNICAL",
        priority: "NORMAL",
      });
      setStep("start");
      setQuery("");
      setExcludedIds([]);
      setConsultedTitles([]);
      setRound(0);
      setView("conversations");
      await loadConversations(created.id);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!selected?.id || !reply.trim()) return;
    try {
      await saasApi.sendSupportMessage(selected.id, { message: reply });
      setReply("");
      await loadMessages();
      await loadConversations();
    } catch (err) { setError(err.message); }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Header title="Soporte" subtitle="Encontrá una solución o escribinos" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Tabs value={view} onChange={(_, value) => setView(value)} sx={{ mb: 2 }}>
        <Tab value="assistant" label="Ayuda automatica" />
        <Tab value="conversations" label={<Stack direction="row" alignItems="center" gap={1}>Mis conversaciones{conversations.some((item) => Number(item.unread_count) > 0) && <Chip size="small" color="error" label={conversations.reduce((sum, item) => sum + Number(item.unread_count || 0), 0)} />}</Stack>} />
      </Tabs>
      {view === "assistant" ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <GlassCard hover={false}>
              <Typography variant="h5" mb={1}>Asistente de ayuda</Typography>
              {step === "start" && (
                <Stack spacing={2}>
                  <Typography color="text.secondary">Contanos brevemente que problema tenes. Te vamos a sugerir pasos y articulos relacionados.</Typography>
                  <TextField label="¿Que necesitas resolver?" multiline minRows={2} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); startSearch(); } }} />
                  <Button variant="contained" endIcon={<SendIcon />} onClick={startSearch} disabled={busy || !query.trim()}>Buscar soluciones</Button>
                </Stack>
              )}
              {step === "results" && (
                <Stack spacing={1.5}>
                  <Typography color="text.secondary">Estas opciones parecen relacionadas con tu consulta:</Typography>
                  {suggestions.map((item) => <Button key={item.id} variant="outlined" onClick={() => openArticle(item)} sx={{ justifyContent: "space-between", textAlign: "left", py: 1.25 }}>{item.title}<Chip size="small" label={item.category} /></Button>)}
                  {!suggestions.length && <Typography color="text.secondary">No encontramos una coincidencia exacta. Podes probar con otras palabras.</Typography>}
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => { setStep("start"); setArticle(null); }}>Cambiar consulta</Button>
                    <Button color="secondary" onClick={showOtherOptions}>{round === 0 ? "No es ninguna de estas opciones" : "Ninguna me sirve"}</Button>
                  </Stack>
                </Stack>
              )}
              {step === "categories" && (
                <Stack spacing={1.5}>
                  <Typography color="text.secondary">Proba buscando por uno de estos temas:</Typography>
                  {categories.map((category) => <Button key={category} variant="outlined" onClick={() => findSolutions(`${query} ${category}`, excludedIds)} sx={{ justifyContent: "flex-start" }}>{category}</Button>)}
                  <Button startIcon={<ArrowBackIcon />} onClick={() => setStep("results")} sx={{ alignSelf: "flex-start" }}>Volver a las opciones anteriores</Button>
                  <Button color="secondary" onClick={() => setStep("escalation")}>No encontre lo que necesito</Button>
                </Stack>
              )}
              {step === "answer" && article && (
                <Stack spacing={2}>
                  <Button startIcon={<ArrowBackIcon />} onClick={() => setStep("results")} sx={{ alignSelf: "flex-start" }}>Volver a opciones</Button>
                  <Chip label={article.category} size="small" sx={{ alignSelf: "flex-start" }} />
                  <Typography variant="h6">{article.title}</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>{article.answer}</Typography>
                  {!!suggestions.length && <><Typography color="text.secondary">Tambien puede servirte:</Typography>{suggestions.slice(0, 3).map((item) => <Button key={item.id} variant="outlined" onClick={() => openArticle(item)} sx={{ justifyContent: "flex-start" }}>{item.title}</Button>)}</>}
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                  <Button variant="contained" onClick={() => { setStep("start"); setQuery(""); setExcludedIds([]); setConsultedTitles([]); setRound(0); }}>Esto resolvio mi problema</Button>
                    <Button onClick={showOtherOptions}>{round === 0 ? "No es ninguna de estas opciones" : "Ninguna me sirve"}</Button>
                  </Stack>
                </Stack>
              )}
              {step === "escalation" && (
                <Stack spacing={2}>
                  <Typography variant="h6">No encontramos la solucion</Typography>
                  <Typography color="text.secondary">Podes reformular la consulta o iniciar una conversacion con un asesor.</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => { setStep("start"); setQuery(""); setExcludedIds([]); setConsultedTitles([]); setRound(0); }}>Probar otra consulta</Button>
                    <Button variant="contained" onClick={startHumanSupport}>Hablar con un asesor</Button>
                  </Stack>
                </Stack>
              )}
              {step === "human" && (
                <Stack spacing={1.5}>
                  <Button startIcon={<ArrowBackIcon />} onClick={() => setStep("escalation")} sx={{ alignSelf: "flex-start" }}>Volver</Button>
                  <Typography color="text.secondary">Tu proximo mensaje abrira una conversacion con el equipo de soporte.</Typography>
                  <TextField label="Asunto" value={humanSubject} onChange={(event) => setHumanSubject(event.target.value)} />
                  <TextField label="Mensaje para el asesor" multiline minRows={3} value={humanMessage} onChange={(event) => setHumanMessage(event.target.value)} />
                  <Button variant="contained" endIcon={<SendIcon />} onClick={createHumanConversation} disabled={busy || !humanSubject.trim() || !humanMessage.trim()}>Enviar y abrir conversacion</Button>
                </Stack>
              )}
            </GlassCard>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <GlassCard hover={false}>
              <Typography variant="h6" mb={2}>Conversaciones</Typography>
              <Stack spacing={1}>
                {conversations.map((item) => <Button key={item.id} variant={selected?.id === item.id ? "contained" : "outlined"} onClick={() => setSelected(item)} sx={{ justifyContent: "space-between", textAlign: "left" }}><span>{item.subject}</span>{item.unread_count ? <Chip label={item.unread_count} color="error" size="small" /> : null}</Button>)}
                {!conversations.length && <Typography color="text.secondary">Todavia no hay conversaciones con soporte.</Typography>}
              </Stack>
            </GlassCard>
          </Grid>
          <Grid item xs={12} lg={8}>
            <GlassCard hover={false}>
              <Box mb={2}>
                <Typography variant="h5">{selected?.subject || "Selecciona una conversacion"}</Typography>
                {selected && <Typography color="text.secondary">{selected.category} · {selected.priority} · {selected.status}</Typography>}
              </Box>
              <Stack spacing={1.5} sx={{ minHeight: 360, maxHeight: 520, overflowY: "auto" }}>
                {messages.map((message) => <Box key={message.id} sx={{ maxWidth: "82%", alignSelf: message.sender_role === "superadmin" ? "flex-start" : "flex-end", p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: "8px", bgcolor: message.sender_role === "superadmin" ? "action.hover" : "primary.main", color: message.sender_role === "superadmin" ? "text.primary" : "primary.contrastText" }}><Typography variant="caption" color="text.secondary">{message.sender_role} · {message.created_at}</Typography><Typography sx={{ whiteSpace: "pre-wrap" }}>{message.message}</Typography></Box>)}
                {!messages.length && <Typography color="text.secondary">No hay mensajes para mostrar.</Typography>}
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} gap={1} mt={2}>
                <TextField label="Responder" value={reply} onChange={(event) => setReply(event.target.value)} fullWidth disabled={!selected} />
                <Button variant="contained" onClick={sendReply} disabled={!selected || !reply.trim()}>Enviar</Button>
              </Stack>
            </GlassCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SupportCenter;
